import { TabletButton } from '@campfire/tablet-button';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Theme,
  Typography,
} from '@material-ui/core';
import { Sync, Lock, Archive } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { isEqual } from 'lodash';
import React, { useMemo, useState, useEffect } from 'react';
import { AlertCard } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ORG_CHAT_SETTINGS } from './admin-console-chat-model.gql';
import { ChatAutoChannelEntityType, useAdminConsoleChatActions } from './admin-console-chat-actions';
import { GetOrgChatSettings } from './__generated__/GetOrgChatSettings';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
      display: 'flex',
      justifyContent: 'space-between',
    },
    headingLeft: {
      flex: 1,
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    sectionHeading: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginBottom: '1rem',
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '0.75rem',
      paddingBottom: '0.75rem',
      borderBottom: `1px solid ${theme.color.grey.pathwayGrey200}`,
      '&:last-of-type': {
        borderBottom: 'none',
      },
    },
    toggleLabel: {
      fontSize: '15px',
      color: '#000000b0',
      fontWeight: 500,
    },
    toggleDescription: {
      fontSize: '13px',
      color: '#808080',
    },
    rowActions: {
      display: 'flex',
      gap: '8px',
      marginLeft: '12px',
      flexShrink: 0,
      alignItems: 'center',
    },
    upgradeContainer: {
      background: 'white',
      padding: '3rem 1.875rem',
      marginTop: '2.25rem',
      textAlign: 'center',
      borderRadius: '4px',
    },
    upgradeIconWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: '#F3E8FF',
      marginBottom: '1rem',
    },
    upgradeTitle: {
      fontWeight: 600,
      fontSize: '20px',
      color: theme.color.grey.neutralBrand900,
      marginBottom: '0.5rem',
    },
    upgradeBody: {
      fontSize: '14px',
      color: '#808080',
      maxWidth: '560px',
      margin: '0 auto 1.5rem',
    },
    dialogTitle: {
      fontWeight: 500,
      fontSize: '18px',
      lineHeight: '30px',
    },
  })
);

type FormValues = {
  chatAutoChannelProgram: boolean;
  chatAutoChannelActivity: boolean;
  chatAutoChannelRole: boolean;
  chatAutoChannelRoster: boolean;
};

const INITIAL_FALLBACK: FormValues = {
  chatAutoChannelProgram: false,
  chatAutoChannelActivity: false,
  chatAutoChannelRole: false,
  chatAutoChannelRoster: false,
};

const UPGRADE_CONTACT_EMAIL = 'hello@volaby.org';

const ENTITY_PLURAL: Record<ChatAutoChannelEntityType, string> = {
  program: 'program',
  activity: 'activity',
  role: 'role',
  roster: 'roster',
};

export const AdminConsoleChatSettings = () => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const {
    runSaveChatSettings,
    runSaveChatSettingsLoading,
    runBackfillChannels,
    runBackfillChannelsLoading,
    runAutoArchiveChannels,
    runAutoArchiveChannelsLoading,
  } = useAdminConsoleChatActions();

  const { data, refetch } = useCampfireQuery<GetOrgChatSettings, undefined>(GET_ORG_CHAT_SETTINGS);

  const chatEnabled = !!data?.tm.tenant.config?.chatEnabled;

  const initialValues: FormValues = useMemo(() => {
    const config = data?.tm.tenant.config;
    if (!config) return INITIAL_FALLBACK;
    return {
      chatAutoChannelProgram: !!config.chatAutoChannelProgram,
      chatAutoChannelActivity: !!config.chatAutoChannelActivity,
      chatAutoChannelRole: !!config.chatAutoChannelRole,
      chatAutoChannelRoster: !!config.chatAutoChannelRoster,
    };
  }, [data]);

  const [values, setValues] = useState<FormValues>(initialValues);
  const [archiveTarget, setArchiveTarget] = useState<ChatAutoChannelEntityType | null>(null);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const hasChanges = !isEqual(values, initialValues);

  const handleToggle = (key: keyof FormValues) => {
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    runSaveChatSettings({
      chatEnabled,
      ...values,
      handleSuccess: () => refetch && refetch(),
    });
  };

  const confirmArchive = () => {
    if (!archiveTarget) return;
    runAutoArchiveChannels(archiveTarget);
    setArchiveTarget(null);
  };

  const renderRow = (
    key: keyof FormValues,
    entityType: ChatAutoChannelEntityType,
    label: string,
    description: string,
    ariaLabel: string,
    showActions: boolean = true
  ) => {
    const isOn = values[key];
    const initiallyOn = initialValues[key];
    const backfillDisabled = !isOn || !initiallyOn || runBackfillChannelsLoading;
    return (
      <Box className={classes.toggleRow}>
        <Switch
          checked={isOn}
          onChange={() => handleToggle(key)}
          color='primary'
          inputProps={{ 'aria-label': ariaLabel }}
        />
        <Box ml={1} flex={1}>
          <Typography className={classes.toggleLabel}>{label}</Typography>
          <Typography className={classes.toggleDescription}>{description}</Typography>
        </Box>
        {showActions && (
          <Box className={classes.rowActions}>
            <TabletButton
              size='small'
              variant='outlined'
              color='primary'
              startIcon={<Sync style={{ fontSize: 16 }} />}
              disabled={backfillDisabled}
              onClick={() => runBackfillChannels(entityType)}
              style={{
                boxSizing: 'border-box',
                borderRadius: '4px',
                padding: '2px 12px',
                textTransform: 'none',
                ...(backfillDisabled
                  ? { border: '1px solid #e0e0e0', color: '#bdbdbd' }
                  : { border: '1px solid #9e9e9e', color: theme.color.grey.neutral300 }),
              }}
            >
              Backfill
            </TabletButton>
            <TabletButton
              size='small'
              variant='outlined'
              color='primary'
              startIcon={<Archive style={{ fontSize: 16 }} />}
              disabled={runAutoArchiveChannelsLoading}
              onClick={() => setArchiveTarget(entityType)}
              style={{
                border: '1px solid #9e9e9e',
                boxSizing: 'border-box',
                borderRadius: '4px',
                padding: '2px 12px',
                color: theme.color.grey.neutral300,
                textTransform: 'none',
              }}
            >
              Archive all
            </TabletButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box className={classes.headingLeft}>
          <Typography variant='h2' className={classes.headingTitle}>
            Chat
          </Typography>
          <Typography variant='body2'>
            Enable in-app messaging between your volunteers and automatically create channels for programs, activities
            and roles.
          </Typography>
        </Box>
      </Box>

      {!chatEnabled ? (
        <Box className={classes.upgradeContainer}>
          <Box className={classes.upgradeIconWrap}>
            <Lock style={{ fontSize: 32, color: theme.color.secondary.main900 }} />
          </Box>
          <Typography className={classes.upgradeTitle}>Want Chat?</Typography>
          <Typography className={classes.upgradeBody}>
            Chat is an add-on that lets your volunteers message each other in-app, with automatic channels for rosters,
            programs, activities and roles. Get in touch to upgrade your plan.
          </Typography>
          <TabletButton
            variant='contained'
            color='primary'
            href={`mailto:${UPGRADE_CONTACT_EMAIL}?subject=Chat%20upgrade%20enquiry`}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '8px 20px',
            }}
          >
            Upgrade to Chat
          </TabletButton>
        </Box>
      ) : (
        <>
          <Box className={classes.contentContainer}>
            <Typography className={classes.sectionHeading}>
              <Sync style={{ fontSize: '14px', paddingRight: '5px' }} /> Auto-Created Channels
            </Typography>
            <Typography className={classes.sectionDescription}>
              Automatically create a dedicated chat channel whenever a new program, activity or role is created. Members
              are kept in sync when enrolments change.
            </Typography>

            <AlertCard variant='info'>
              <Typography className={classes.infoBody}>
                Toggling any of these on will create channels for new entities going forward. Use Backfill to
                retroactively create channels for existing entities, or Archive all to remove every auto-created channel
                for that type. Save your toggle change before backfilling.
              </Typography>
            </AlertCard>

            {renderRow(
              'chatAutoChannelProgram',
              'program',
              'Programs',
              'Create a channel for each program; enrolment follows program enrolment.',
              'auto channels for programs'
            )}
            {renderRow(
              'chatAutoChannelActivity',
              'activity',
              'Activities',
              'Create a channel for each activity; enrolment follows the activity team.',
              'auto channels for activities'
            )}
            {renderRow(
              'chatAutoChannelRole',
              'role',
              'Roles',
              'Create a channel for each role; enrolment follows volunteers assigned to that role.',
              'auto channels for roles'
            )}
            {renderRow(
              'chatAutoChannelRoster',
              'roster',
              'Rosters',
              'Create a channel for each published roster; members follow roster additions and removals. Channels are auto-archived after the roster date.',
              'auto channels for rosters',
              false
            )}
          </Box>

          {hasChanges ? (
            <Box display='flex' justifyContent='right' pt={2} pb={4}>
              <TabletButton
                type='button'
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={runSaveChatSettingsLoading}
                style={{
                  backgroundColor: theme.color.secondary.main900,
                  color: '#ffffff',
                  borderRadius: '4px',
                  textTransform: 'none',
                  padding: '7px 12px',
                  minWidth: '40px',
                }}
              >
                Save
              </TabletButton>
            </Box>
          ) : null}

          <Dialog open={archiveTarget !== null} onClose={() => setArchiveTarget(null)} maxWidth='xs' fullWidth>
            <DialogTitle disableTypography>
              <Typography className={classes.dialogTitle}>Archive all auto-created channels?</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <DialogContentText>
                Every auto-created {archiveTarget ? ENTITY_PLURAL[archiveTarget] : ''} channel will be archived in the
                background. Members will lose access to the conversation history.
              </DialogContentText>
              <DialogContentText>This cannot be undone.</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Box padding={2}>
                <TabletButton
                  variant='text'
                  color='error'
                  onClick={() => setArchiveTarget(null)}
                  style={{ marginRight: 8, textTransform: 'none' }}
                >
                  Cancel
                </TabletButton>
                <TabletButton
                  variant='contained'
                  color='primary'
                  startIcon={<Archive />}
                  onClick={confirmArchive}
                  disabled={runAutoArchiveChannelsLoading}
                  style={{ textTransform: 'none' }}
                >
                  Archive all
                </TabletButton>
              </Box>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};
